import React from 'react'
import { Stack } from 'react-bootstrap'
import { Box, HorizontalStack, ShowIf } from '@ztechapps/webui.util'

export type EntityCreatedMessageProps = {
  /** Heading label, e.g. "Order Created:" or "PO Created:". */
  label: string
  /** Entity code shown as the clickable / plain text. */
  code: string
  /** Accessible label for the anchor, e.g. "View order ABC" or "View purchase order PO-123". */
  ariaLabel: string
  /** Pre-resolved detail URL. Absent → renders `code` as plain text. */
  url?: string
  /** Optional attachment status note shown below the heading. */
  fileAttached?: boolean
}

/**
 * Pure presentational renderer for the "<entity> Created" structured chat
 * results emitted by `createOrder` / `createPurchaseOrder` MCP tools (and any
 * future "X Created" tools). Knows nothing about chat context — callers
 * resolve the URL out-of-band and pass it in.
 *
 * Wrappers (`OrderCreatedMessage`, `PurchaseOrderCreatedMessage`) supply the
 * label / ariaLabel / URL via `useAiChatContext()` callbacks.
 */
export const EntityCreatedMessage = ({ label, code, ariaLabel, url, fileAttached }: EntityCreatedMessageProps) => (
  <Box className="bg-body">
    <Stack gap={1}>
      <HorizontalStack gap={2} className="align-items-center">
        <span className="fw-semibold text-body">{label}</span>
        <ShowIf condition={!!url}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none"
            aria-label={ariaLabel}
          >
            {code}
          </a>
        </ShowIf>
        <ShowIf condition={!url}>
          <span className="text-body">{code}</span>
        </ShowIf>
      </HorizontalStack>

      <ShowIf condition={fileAttached === true}>
        <div className="small text-body-secondary">File attached.</div>
      </ShowIf>
      <ShowIf condition={fileAttached === false}>
        <div className="small text-warning-emphasis">Warning: File attachment failed — please attach manually.</div>
      </ShowIf>
    </Stack>
  </Box>
)
